import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  effect,
} from '@angular/core';
import { OpsCandidatesService } from './ops-candidates.service';
import { Candidates } from '@interfaces/candidates.model';
import { columnDefs, columnsForExport } from './ops-candidates.config';
import { ExportService } from '@services/export.service';
import { dateFormat } from '@constants/dateFormat';
import { FileDownloadService } from '@services/file-download.service';
import { PersonaType } from '@constants/global.enum';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { LoaderService } from '@app/shared/services/loader.service';

@Component({
  selector: 'app-ops-candidates',
  templateUrl: './ops-candidates.component.html',
  styleUrl: './ops-candidates.component.scss',
})
export class OpsCandidatesComponent implements OnInit {
  @Output() candidatesCountChange = new EventEmitter<number>();

  private isDataChanging = false;
  private isClearing = false;

  // op-table pager state (server-side pages)
  pageSize = 10;

  searchFieldOptions = [
    { label: 'Candidate Name', value: 'candidateName' },
    { label: 'Vendor', value: 'vendorName' },
    { label: 'Position', value: 'jobTitle' },
    { label: 'Position ID', value: 'positionId' },
    { label: 'Status', value: 'status' },
    { label: 'Location', value: 'location' },
    { label: 'Email', value: 'emailId' },
    { label: 'Hiring Manager', value: 'hiringManagerName' },
  ];
  selectedSearchField = '';
  searchFieldDropdownVisible = true;
  private currentSearchKey = '';
  private currentSearchValue = '';

  columnDefs = columnDefs;
  rowData: Candidates[] = [];
  pagedCandidatesData: Candidates[] = [];
  selectedRows: Candidates[] = [];
  headerChecked = false;
  isFetchingAll = false;
  private allCandidatesForDownload: Candidates[] = [];
  totalRecords = 0;
  currentPage = 1;

  filterValue = '';
  isDataLoaded = false;
  fieldsVisibility: string = PersonaType.OpsCandidates;
  imageSrc = 'assets/img/no-data-found.png';
  urlDisplayLimit = 15;
  dateFormat = dateFormat;

  @ViewChild('historyDialog') historyDialogTpl!: TemplateRef<any>;
  private historyDialogRef?: MatDialogRef<any>;
  historyMessage = '';
  historySubmissions: Array<{
    jobId: string;
    jobTitle: string;
    hiringManager: string;
    status: string;
    feedback: string;
    vendorName: string;
    submittedDate: string;
  }> = [];

  constructor(
    private candidatesService: OpsCandidatesService,
    private cdr: ChangeDetectorRef,
    private fileDownloadService: FileDownloadService,
    private dialog: MatDialog,
    private loaderService: LoaderService,
    private exportService: ExportService,
  ) {
    effect(() => {
      const signalData = this.candidatesService.candidatesSignal();
      if (signalData?.data?.data) {
        this.totalRecords = signalData.data.totalRecords;
        this.rowData = signalData.data.data.map((row: Candidates) => ({ ...row, selected: false }));
        this.pagedCandidatesData = this.rowData;

        this.isDataLoaded = true;
        this.isDataChanging = true;
        this.candidatesCountChange.emit(signalData.data.totalRecords);
        this.loaderService.hide();
        this.cdr.markForCheck();
        setTimeout(() => { this.isDataChanging = false; }, 100);
      }
    });
  }

  ngOnInit(): void {
    this.loaderService.show();
    this.fetchCandidates(1, this.pageSize);
  }

  private fetchCandidates(page: number, size: number, key?: string, value?: string): void {
    this.currentPage = page;
    this.selectedRows = [];
    this.allCandidatesForDownload = [];
    this.headerChecked = false;
    this.candidatesService.getCandidates(page, size, key, value);
  }

  onSearchFieldChange(option: any): void {
    const incoming = typeof option === 'object' ? option?.value : option;
    if (incoming === this.selectedSearchField) {
      this.selectedSearchField = '';
      this.filterValue = '';
      this.currentSearchKey = '';
      this.currentSearchValue = '';
      this.searchFieldDropdownVisible = false;
      setTimeout(() => { this.searchFieldDropdownVisible = true; }, 0);
      this.fetchCandidates(1, this.pageSize);
      return;
    }

    this.selectedSearchField = incoming;
    if (this.currentSearchValue) {
      this.currentSearchKey = this.selectedSearchField;
      this.fetchCandidates(1, this.pageSize, this.currentSearchKey, this.currentSearchValue);
    }
  }

  // ── op-table sorting (orders the rows of the current page) ────────────
  sortField: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
  }

  get displayRows(): Candidates[] {
    const data = [...(this.pagedCandidatesData || [])];
    const f = this.sortField;
    if (!f) return data;
    data.sort((a: any, b: any) => {
      const av = a?.[f];
      const bv = b?.[f];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }

  // ── op-table pager (server-side pages) ────────────────────────────────
  get totalPages(): number {
    return Math.max(1, Math.ceil((this.totalRecords || 0) / this.pageSize));
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  /** Page numbers to render; -1 is an ellipsis gap. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.currentPage;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page === -1 || page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.fetchCandidates(
      page,
      this.pageSize,
      this.currentSearchKey || undefined,
      this.currentSearchValue || undefined,
    );
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.fetchCandidates(
      1,
      this.pageSize,
      this.currentSearchKey || undefined,
      this.currentSearchValue || undefined,
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  /* checkbox selection — same semantics as the emerald table had:
     header checkbox = "everything matching the search" (fetches all pages
     for download); row checkboxes = explicit picks */
  isSelected(row: Candidates): boolean {
    return this.headerChecked || this.selectedRows.some((r) => r.emailId === row.emailId);
  }

  toggleAllPage(checked: boolean): void {
    if (checked) {
      this.headerChecked = true;
      this.selectedRows = [];
      this.fetchAllForDownload();
    } else {
      this.headerChecked = false;
      this.allCandidatesForDownload = [];
    }
  }

  toggleRow(row: Candidates, checked: boolean): void {
    this.headerChecked = false;
    this.allCandidatesForDownload = [];
    if (checked) {
      if (!this.selectedRows.some((r) => r.emailId === row.emailId)) {
        this.selectedRows = [...this.selectedRows, row];
      }
    } else {
      this.selectedRows = this.selectedRows.filter((r) => r.emailId !== row.emailId);
    }
  }

  onSearchValueChange(value: string): void {
    if (this.isClearing) return;
    this.filterValue = value;
    const trimmed = value?.trim() ?? '';
    if (trimmed.length > 2 || trimmed.length === 0) {
      this.currentSearchKey = trimmed ? this.selectedSearchField : '';
      this.currentSearchValue = trimmed;
      this.fetchCandidates(
        1,
        this.pageSize,
        this.currentSearchKey || undefined,
        this.currentSearchValue || undefined,
      );
    }
  }

  clearInput(): void {
    this.isClearing = true;
    this.filterValue = '';
    this.currentSearchKey = '';
    this.currentSearchValue = '';
    this.isClearing = false;
    this.fetchCandidates(1, this.pageSize);
  }

  handleDataFromChild(data: Candidates[]): void {
    this.pagedCandidatesData = data;
    this.cdr.markForCheck();
  }

  receiveData(data: string, candidateEmailId: string): void {
    this.pagedCandidatesData?.forEach((item) => {
      if (item.emailId === candidateEmailId) {
        item.status = data;
      }
    });
  }

  downloadFile(filePath: string, fileName: string, fileType: string): void {
    const fullFileName = `${fileType}_${fileName}.pdf`;
    this.fileDownloadService.downloadFile(filePath, fullFileName);
  }

  private fetchAllForDownload(): void {
    const BATCH_SIZE = 500;
    const totalPages = Math.ceil(this.totalRecords / BATCH_SIZE);
    this.isFetchingAll = true;
    this.allCandidatesForDownload = [];
    this.cdr.markForCheck();

    const requests = Array.from({ length: totalPages }, (_, i) =>
      this.candidatesService.fetchPage(
        i + 1,
        BATCH_SIZE,
        this.currentSearchKey || undefined,
        this.currentSearchValue || undefined,
      )
    );

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        this.allCandidatesForDownload = responses.flatMap(res => res?.data?.data || []);
        this.isFetchingAll = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isFetchingAll = false;
        this.headerChecked = false;
        this.allCandidatesForDownload = [];
        this.cdr.markForCheck();
      },
    });
  }

  get canDownload(): boolean {
    return this.allCandidatesForDownload.length > 0 || this.selectedRows.length > 0;
  }

  download(): void {
    const dataToExport = this.allCandidatesForDownload.length > 0
      ? this.allCandidatesForDownload
      : this.selectedRows;
    this.exportService.handleExport(dataToExport, columnsForExport, 'ops-candidates');
  }

  viewHistory(eachRow: Candidates): void {
    const mobile = (eachRow as any)?.contactNumber || '';
    if (!eachRow?.emailId) {
      this.historyMessage = 'Unable to fetch history: missing email.';
      this.historySubmissions = [];
      this.openHistoryDialog();
      return;
    }
    this.candidatesService.validateProfileHistory({
      email: eachRow.emailId,
      mobileNumber: mobile,
      labelName: 'history',
    }).subscribe({
      next: (res: any) => {
        if (res?.status === 'SUCCESS') {
          const data = res?.data;
          this.historyMessage = res?.message || data?.message || '';
          this.historySubmissions = Array.isArray(data?.submissions)
            ? data.submissions
            : [];
        } else {
          this.historyMessage = res?.message || 'No history available';
          this.historySubmissions = [];
        }
        this.openHistoryDialog();
      },
      error: (err) => {
        this.historyMessage = err?.error?.message || 'Failed to load history';
        this.historySubmissions = [];
        this.openHistoryDialog();
      },
    });
  }

  private openHistoryDialog(): void {
    this.historyDialogRef = this.dialog.open(this.historyDialogTpl, {
      width: '720px',
      maxWidth: '90vw',
      panelClass: 'center-dialog',
    });
  }

  closeHistoryDialog(): void {
    this.historyDialogRef?.close();
  }

  truncateText(text: string, maxLength = 30): string {
    if (!text || text === 'null' || text === 'undefined') return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  getTooltipText(text: string): string | null {
    if (!text || text === 'null' || text === 'undefined' || text.trim() === '') return null;
    return text;
  }
}